import React from 'react';
import { Users } from 'lucide-react';
import { useI18n } from '../../utils/i18n';
import apiService from '../../services/apiService';
import { useToast } from '../ToastProvider';

// #312: Reiter „Benutzerverwaltung“ des Admin-Bereichs, Code unverändert aus
// AdminPanel.js übernommen.
//
// Anders als die übrigen Reiter ohne eigenen Hook: Er hat keinen Zustand, der
// nur ihm gehört. Die Benutzerliste lädt `loadData()` im Panel, und der
// API-Keys-Reiter braucht sie ebenfalls. Was bleibt, ist der Rollen-Handler,
// und der arbeitet auf den Settern, die das Panel hereinreicht.
const UsersTab = ({ users, setUsers, loading, saving, setSaving, setError }) => {
  const { getLocale, t } = useI18n();
  const { pushToast } = useToast();

  const handleUserRoleUpdate = async (userId, field, value) => {
    setSaving(true);
    setError(null);
    
    try {
      await apiService.updateUserRole(userId, { [field]: value });
      
      // Update local state
      setUsers(prevUsers => 
        prevUsers.map(user => 
          user.id === userId ? { ...user, [field]: value } : user
        )
      );
      
      pushToast({ message: t('admin.users.roleUpdated', 'Benutzerrolle erfolgreich aktualisiert.'), type: 'success' });
    } catch (error) {
      console.error('Error updating user role:', error);
      setError(t('admin.users.roleUpdateError', 'Fehler beim Aktualisieren der Benutzerrolle.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="admin-section">
      <h3>{t('admin.tabs.users', 'Benutzerverwaltung')}</h3>
      
      <div className="users-table-container">
        <table className="users-table">
          <thead>
            <tr>
              <th>{t('admin.users.email', 'E-Mail')}</th>
              <th>{t('admin.users.name', 'Name')}</th>
              <th>{t('admin.users.lastLogin', 'Letzter Login')}</th>
              {/* #82: Rollen-Tooltips nach dem Zielmodell v3 (#44) */}
              <th title={t('admin.users.adminTooltip', 'Zugang zum Admin-Bereich (rein technisch: Benutzer, Postfächer, KI, API-Keys). Gibt KEINE Ticketrechte — dafür zusätzlich Ticketworker/Manager setzen.')}>{t('admin.users.admin', 'Admin')}</th>
              <th title={t('admin.users.managerTooltip', 'Wie Ticketworker, zusätzlich: fremde Tickets bearbeiten, abschließen und mergen, Tickets löschen, alle Kommentare bearbeiten, „Alle Tickets"/„Alle Kalender", andere einplanen, Kundenverwaltung.')}>{t('admin.users.manager', 'Manager')}</th>
              <th title={t('admin.users.ticketworkerTooltip', 'Darf eigene Tickets (zugewiesene und selbst erstellte) bearbeiten und abschließen, Pool-Tickets übernehmen, Tickets übergeben und sich selbst einplanen. Ohne dieses Häkchen: nur lesen.')}>{t('admin.users.ticketworker', 'Ticketworker')}</th>
              <th title={t('admin.users.readonlyTooltip', 'Nur-Lese-Konto: überstimmt ALLE anderen Rollen — sehen ja, ändern nein (auch als Manager). Feedback geben und eigene Ansichts-Einstellungen bleiben möglich.')}>{t('admin.users.readonly', 'Readonly')}</th>
              <th title={t('admin.users.licensedTooltip', 'Lizenzplatz: taucht in der Zuweisen-Auswahl der Tickets auf. Keine weiteren Auswirkungen.')}>{t('admin.users.licensed', 'Lizenziert')}</th>
              <th title={t('admin.users.activeTooltip', 'Darf sich anmelden. Deaktiviert = Anmeldung und alle API-Zugriffe blockiert.')}>{t('admin.users.active', 'Aktiv')}</th>
            </tr>
          </thead>
          <tbody>
            {users.map(user => (
              <tr key={user.id}>
                <td className="user-email">{user.email}</td>
                <td>{user.displayName || `${user.firstName || ''} ${user.lastName || ''}`.trim() || '-'}</td>
                <td>{user.lastLogin ? new Date(user.lastLogin).toLocaleString(getLocale()) : t('common.never', 'Nie')}</td>
                <td>
                  <label className="role-checkbox">
                    <input
                      type="checkbox"
                      checked={user.isAdmin}
                      onChange={(e) => handleUserRoleUpdate(user.id, 'isAdmin', e.target.checked)}
                      disabled={saving || user.isSystemAdmin}
                      title={user.isSystemAdmin ? t('admin.users.systemAdminTooltip', 'System-Administrator-Rechte können nicht entfernt werden') : ''}
                    />
                    <span className={`checkmark ${user.isSystemAdmin ? 'system-admin' : ''}`}></span>
                    {user.isSystemAdmin && (
                      <span className="system-admin-badge" title={t('admin.users.systemAdminBadge', 'System-Administrator (nicht entfernbar)')}>
                        👑
                      </span>
                    )}
                  </label>
                </td>
                <td>
                  <label className="role-checkbox">
                    <input
                      type="checkbox"
                      checked={user.isManager}
                      onChange={(e) => handleUserRoleUpdate(user.id, 'isManager', e.target.checked)}
                      disabled={saving}
                    />
                    <span className="checkmark"></span>
                  </label>
                </td>
                <td>
                  <label className="role-checkbox">
                    <input
                      type="checkbox"
                      checked={user.isTicketworker}
                      onChange={(e) => handleUserRoleUpdate(user.id, 'isTicketworker', e.target.checked)}
                      disabled={saving}
                    />
                    <span className="checkmark"></span>
                  </label>
                </td>
                <td>
                  <label className="role-checkbox">
                    <input
                      type="checkbox"
                      checked={user.isReadonly}
                      onChange={(e) => handleUserRoleUpdate(user.id, 'isReadonly', e.target.checked)}
                      disabled={saving}
                    />
                    <span className="checkmark"></span>
                  </label>
                </td>
                <td>
                  <label className="role-checkbox">
                    <input
                      type="checkbox"
                      checked={user.isLicensed}
                      onChange={(e) => handleUserRoleUpdate(user.id, 'isLicensed', e.target.checked)}
                      disabled={saving}
                    />
                    <span className="checkmark"></span>
                  </label>
                </td>
                <td>
                  <label className="role-checkbox">
                    <input
                      type="checkbox"
                      checked={user.isActive}
                      onChange={(e) => handleUserRoleUpdate(user.id, 'isActive', e.target.checked)}
                      disabled={saving}
                    />
                    <span className="checkmark"></span>
                  </label>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      
      {users.length === 0 && !loading && (
        <div className="empty-state">
          <Users size={48} style={{ color: 'var(--color-text-muted)', marginBottom: '16px' }} />
          <p>{t('admin.users.none', 'Keine Benutzer gefunden.')}</p>
        </div>
      )}
    </div>
  );
};

export default UsersTab;
